import { EmploymentStatus } from '@zorro/clients';
import { PillColor } from '@zorro/design-system/v2/Pill';
import { FlagIcon } from '@zorro/shared/assets';
import {
  formatDateEnLocale,
  getNow,
  parseDateISO,
} from '@zorro/shared/formatters';
import { DeepReadonly } from '@zorro/types';
import { ReactElement } from 'react';
import { $enum } from 'ts-enum-util';

const NOT_AVAILABLE = '-';
const flagStyles = { minWidth: '16px' };

export const eligibleLabel = 'Eligible for benefits';
export const ineligibleLabel = 'Ineligible for benefits';

export const benefitEligibilityLabelConfig: DeepReadonly<{
  [key in EmploymentStatus]: string;
}> = {
  [EmploymentStatus.INELIGIBLE_WAITING]: 'Waiting for benefit eligibility',
  [EmploymentStatus.INELIGIBLE_ON_LEAVE]: 'Benefit eligibility paused',
  [EmploymentStatus.ELIGIBLE_EMPLOYED]: eligibleLabel,
  [EmploymentStatus.ELIGIBLE_ENDING]: `${eligibleLabel} - ending`,
  [EmploymentStatus.ELIGIBLE_UPCOMING_LEAVE]: `${eligibleLabel} - upcoming leave`,
  [EmploymentStatus.INELIGIBLE_TERMINATED]: `${ineligibleLabel} - terminated`,
  [EmploymentStatus.INELIGIBLE]: ineligibleLabel,
};

export const getEligibilityStatusBadgeConfig = (
  eligibilityStatus?: EmploymentStatus | null,
  eligibleFrom?: string | null,
  eligibleUntil?: string | null,
  leaveOfAbsenceStartDate?: string | null,
  leaveOfAbsenceEndDate?: string | null,
  createdAt?: string,
  terminationDate?: string | null,
  hasPreviousEligibility?: boolean | null
) => {
  const label = eligibilityStatus
    ? benefitEligibilityLabelConfig[eligibilityStatus]
    : NOT_AVAILABLE;

  const createdAtFallback = createdAt
    ? formatDateEnLocale(createdAt)
    : NOT_AVAILABLE;

  return $enum.mapValue(eligibilityStatus).with<{
    label: string;
    icon?: ReactElement;
    color: PillColor;
    description?: string;
  }>({
    [EmploymentStatus.INELIGIBLE_WAITING]: {
      label,
      icon: <FlagIcon style={flagStyles} flagColor="transparent" grow />,
      color: 'yellow',
      description: eligibleFrom
        ? `Eligibility will start on ${formatDateEnLocale(eligibleFrom)}`
        : undefined,
    },
    [EmploymentStatus.INELIGIBLE_ON_LEAVE]: {
      label,
      icon: <FlagIcon style={flagStyles} flagColor="transparent" grow />,
      color: 'yellow',
      description: leaveOfAbsenceEndDate
        ? `On leave of absence until ${formatDateEnLocale(
            leaveOfAbsenceEndDate
          )}`
        : undefined,
    },
    [EmploymentStatus.ELIGIBLE_EMPLOYED]: {
      label,
      icon: <FlagIcon style={flagStyles} flagColor="transparent" grow />,
      color: 'green',
      description: eligibleFrom
        ? `Eligibility started on ${formatDateEnLocale(eligibleFrom)}`
        : `Eligibility started on ${createdAtFallback}`,
    },
    [EmploymentStatus.ELIGIBLE_ENDING]: {
      label,
      icon: (
        <FlagIcon style={flagStyles} flagColor="transparent" withDot grow />
      ),
      color: 'green',
      description: eligibleUntil
        ? `Eligibility ending on ${formatDateEnLocale(eligibleUntil)}`
        : undefined,
    },
    [EmploymentStatus.ELIGIBLE_UPCOMING_LEAVE]: {
      label,
      icon: (
        <FlagIcon style={flagStyles} flagColor="transparent" withDot grow />
      ),
      color: 'green',
      description:
        leaveOfAbsenceStartDate && leaveOfAbsenceEndDate
          ? `Upcoming leave between ${formatDateEnLocale(
              leaveOfAbsenceStartDate
            )} and ${formatDateEnLocale(leaveOfAbsenceEndDate)}`
          : undefined,
    },
    [EmploymentStatus.INELIGIBLE_TERMINATED]: {
      label,
      icon: <FlagIcon style={flagStyles} flagColor="transparent" grow />,
      color: 'red',
      description: eligibleUntil
        ? `Eligibility ended on ${formatDateEnLocale(eligibleUntil)}`
        : undefined,
    },
    [EmploymentStatus.INELIGIBLE]: {
      label,
      icon: <FlagIcon style={flagStyles} flagColor="transparent" grow />,
      color: 'red',
      description: (() => {
        if (!eligibleUntil) return undefined;
        if (
          eligibleFrom &&
          parseDateISO(eligibleFrom).isAfter(parseDateISO(eligibleUntil), 'day')
        ) {
          return !hasPreviousEligibility && !terminationDate
            ? 'Ended prior to start date'
            : 'Terminated before activation';
        }
        const isStillEligible = parseDateISO(eligibleUntil).isSameOrAfter(
          getNow(),
          'day'
        );
        return `Eligibility ${isStillEligible ? 'ending' : 'ended'} on ${formatDateEnLocale(eligibleUntil)}`;
      })(),
    },
    [$enum.handleNull]: {
      label,
      color: 'gray',
      description: NOT_AVAILABLE,
    },
    [$enum.handleUndefined]: {
      label,
      color: 'gray',
      description: NOT_AVAILABLE,
    },
    [$enum.handleUnexpected]: {
      label,
      color: 'gray',
      description: NOT_AVAILABLE,
    },
  });
};
